import {
  provideHttpClient,
  withInterceptors,
  withInterceptorsFromDi,
} from '@angular/common/http';
import {
  ApplicationConfig,
  inject,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideZoneChangeDetection,
} from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import {
  provideRouter,
  withComponentInputBinding,
  withInMemoryScrolling,
  withViewTransitions,
} from '@angular/router';
import { provideHotToastConfig } from '@ngxpert/hot-toast';
import {
  CellStyleModule,
  ColumnAutoSizeModule,
  CustomFilterModule,
  InfiniteRowModelModule,
  LocaleModule,
  ModuleRegistry,
  NumberFilterModule,
  PaginationModule,
  provideGlobalGridOptions,
  RowApiModule,
  TextFilterModule,
} from 'ag-grid-community';
import { appRoutes } from './app.routes';
import { provideMsalConfig } from './auth-config';
import { AuthService } from './auth/auth-service';
import {
  httpErrorInterceptor,
  provideThrottlingInterceptor,
} from './interceptors';
import { provideDataService } from './providers/data-service-provider';
import { provideMaterialDefaultOptions } from './providers/mat-default-options-provider';
import { provideQuillEditorDefaults } from './providers/quill-editor-provider';
import { provideUpuTransloco } from './providers/transloco-provider';
import { provideAppTitleStrategy } from './router/app-title-strategy';
import { viewTransitionOptions } from './router/view-transition-options';
import { AG_GRID_GLOBAL_OPTIONS } from './utils/ag-grid-global-options.constant';

const initializeApp = () => {
  const authService = inject(AuthService);
  const matIconRegistry = inject(MatIconRegistry);

  matIconRegistry.setDefaultFontSetClass('material-symbols-rounded');

  return authService.initialize();
};

ModuleRegistry.registerModules([
  TextFilterModule,
  CellStyleModule,
  NumberFilterModule,
  RowApiModule,
  LocaleModule,
  ColumnAutoSizeModule,
  PaginationModule,
  InfiniteRowModelModule,
  CustomFilterModule,
]);

provideGlobalGridOptions(AG_GRID_GLOBAL_OPTIONS);

export const appConfig: ApplicationConfig = {
  providers: [
    provideAppInitializer(initializeApp),
    provideBrowserGlobalErrorListeners(),
    provideZoneChangeDetection({ eventCoalescing: true, runCoalescing: true }),
    provideRouter(
      appRoutes,
      withComponentInputBinding(),
      withViewTransitions(viewTransitionOptions),
      withInMemoryScrolling({
        scrollPositionRestoration: 'enabled',
        anchorScrolling: 'enabled',
      }),
    ),
    provideHttpClient(
      withInterceptors([httpErrorInterceptor]),
      withInterceptorsFromDi(),
    ),
    provideThrottlingInterceptor({
      maxConcurrentRequests: 10,
      maxRetries: 1,
      baseRetryDelay: 1000,
    }),
    provideMaterialDefaultOptions(),
    provideAppTitleStrategy(),
    provideUpuTransloco(),
    provideHotToastConfig({
      dismissible: true,
      duration: 5000,
      position: 'top-right',
      className: 'upu-toast',
    }),
    provideDataService(),
    provideMsalConfig(),
    provideQuillEditorDefaults(),
  ],
};
